import type { MotionDiag, MotionOutcome } from '../../services/adminApi'
import styles from './MotionLadder.module.css'

/**
 * One gesture tag's journey, rendered as a vertical ladder.
 *
 * Not a table: the pipeline has a variable number of stages — a preset skips
 * generation, a decline stops at the first rung — and fixed columns force empty
 * cells that read as MISSING data rather than a skipped stage. A two-rung block
 * is visibly a different shape from a three-rung one.
 *
 * Colour is a redundant channel only; every state is also readable as text.
 */

const OUTCOME_LABEL: Record<MotionOutcome, string> = {
  matched: 'vocabulary match',
  served: 'clip served',
  preset: 'preset played',
  declined: 'nothing animatable',
  not_served: 'no clip served',
  corrupt_tracks: 'no clip: corrupt tracks',
  error: 'error',
  rewrite_failed: 'no clip: rewrite failed',
}

// good | neutral | bad — drives colour, which is never the only signal.
const OUTCOME_TONE: Record<MotionOutcome, string> = {
  matched: 'neutral',
  served: 'good',
  preset: 'good',
  declined: 'neutral',
  not_served: 'bad',
  corrupt_tracks: 'bad',
  error: 'bad',
  rewrite_failed: 'bad',
}

function DurationChip({ diag }: { diag: MotionDiag }) {
  if (!diag.durationSec) return null
  // A length the character asked for is intent; one the rewriter inferred is a
  // guess.
  const title =
    diag.durationSource === 'tag'
      ? 'from the tag — the character asked for this length'
      : diag.durationSource === 'rewriter'
        ? 'inferred by the rewriter from the motion'
        : "gesturegen's default"
  return (
    <span className={styles.duration} title={title}>
      {diag.durationSec}s
      {diag.durationSource ? <em className={styles.durationSrc}>{diag.durationSource}</em> : null}
    </span>
  )
}

export function MotionLadder({ diag }: { diag: MotionDiag }) {
  // A discarded attempt gets no outcome colour: whatever it resolved to, it was
  // never performed, and tinting it green would claim otherwise.
  const tone = diag.discarded ? 'discarded' : (OUTCOME_TONE[diag.outcome] ?? 'neutral')
  // A vocabulary match never reaches the rewriter, so it has no middle rung.
  const showRewrite = !diag.matched
  const rewroteTo = diag.preset || diag.rewritten

  return (
    <div className={`${styles.block} ${styles[tone]}`}>
      <div className={styles.rung}>
        <code className={styles.rawTag}>*{diag.rawTag}*</code>
        <span className={styles.badges}>
          {diag.discarded ? (
            <span
              className={styles.discardBadge}
              title="the LLM reply this came from stripped to empty text and was retried — this motion was processed but never performed"
            >
              discarded
            </span>
          ) : null}
          {diag.tagSource === 'bracket_promoted' ? (
            <span className={styles.srcBadge} title="promoted out of a [bracket] audio tag">
              bracket
            </span>
          ) : null}
          <span className={styles.elapsed} title="rewriter + gesturegen for this tag. Rewrites run concurrently across a turn's tags, so a turn costs less than the sum of these.">
            {(diag.rewriteMs ?? 0) + (diag.resolveMs ?? 0)}ms
          </span>
        </span>
      </div>

      {showRewrite ? (
        <div className={styles.step}>
          <span className={styles.arrow}>↓</span>
          <span className={styles.stepLabel}>rewrite</span>
          {diag.rewriteMs ? (
            <span className={styles.stepMs} title="motion rewriter round trip">{diag.rewriteMs}ms</span>
          ) : null}
          {rewroteTo ? (
            <code className={styles.result}>{rewroteTo}</code>
          ) : diag.outcome === 'declined' ? (
            <span className={styles.muted}>declined</span>
          ) : (
            <span className={styles.muted}>unchanged</span>
          )}
          <DurationChip diag={diag} />
        </div>
      ) : null}

      {/* A preset plays its own baked clip, so it has no generate/pool rung —
          the missing rung IS the signal. Elsewhere the label separates a fresh
          kimodo clip from a pool hit. */}
      {diag.outcome === 'served' || diag.outcome === 'corrupt_tracks' ? (
        <div className={styles.step}>
          <span className={styles.arrow}>↓</span>
          <span className={styles.stepLabel}>{diag.generated ? 'generate' : 'pool hit'}</span>
          {diag.resolveMs ? (
            <span
              className={styles.stepMs}
              title={diag.generated
                ? 'kimodo /generate round trip — a fresh clip was made for this tag'
                : 'gesturegen pool lookup; no generation was needed'}
            >
              {diag.resolveMs}ms
            </span>
          ) : null}
          {diag.motionClass ? (
            <span
              className={styles.classBadge}
              title="performance clips travel and take the override channel; gesture clips are additive"
            >
              {diag.motionClass}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className={styles.outcome}>
        <span className={styles.outcomeLabel}>{OUTCOME_LABEL[diag.outcome] ?? diag.outcome}</span>
        {diag.preset ? <code className={styles.presetName}>{diag.preset}</code> : null}
        {diag.poolEntryId ? (
          <span className={styles.entry} title={`pool entry ${diag.poolEntryId}`}>
            entry {diag.poolEntryId.slice(0, 8)}
          </span>
        ) : null}
        {diag.error ? <span className={styles.errText}>{diag.error}</span> : null}
      </div>
    </div>
  )
}

/** Compact summary for a collapsed turn: enough to decide whether to open it. */
export function MotionSummary({ diags }: { diags: MotionDiag[] }) {
  if (diags.length === 0) return <span className={styles.summaryMuted}>no motion</span>
  // Discarded records are excluded rather than counted as dropped: they belong
  // to a reply that was never spoken, so they are not this turn's failure.
  const live = diags.filter(d => !d.discarded)
  const discarded = diags.length - live.length
  const played = live.filter(d => d.outcome === 'served' || d.outcome === 'preset' || d.outcome === 'matched').length
  const dropped = live.length - played
  return (
    <span className={styles.summary}>
      {live.length} tag{live.length === 1 ? '' : 's'} · {played} played
      {dropped > 0 ? <span className={styles.summaryBad}> · {dropped} dropped</span> : null}
      {discarded > 0 ? (
        <span className={styles.summaryMuted} title="from a retried LLM reply that was never spoken">
          {' '}· {discarded} discarded
        </span>
      ) : null}
    </span>
  )
}
